import * as React from "react";
import { useRouter, useRouterState } from "@tanstack/react-router";
import { PostHogProvider, usePostHog } from "posthog-js/react";

import { TooltipProvider } from "@/components/ui/Tooltip";
import { TailwindIndicator } from "@/components/utils/development-tools/TailwindIndicator";
import { useSessionContext } from "@/hooks/useSession";
import { api, TRPCReactProvider } from "@/trpc/react";

export function ClientProviders({ children }: { children: React.ReactNode }) {
  // On the server (during SPA shell prerendering), skip client-only providers
  if (typeof window === "undefined") return <>{children}</>;

  return (
    <PostHogProvider
      apiKey={import.meta.env.VITE_POSTHOG_API_KEY}
      options={{
        defaults: "2025-05-24",
        api_host: import.meta.env.VITE_POSTHOG_API_HOST,
        person_profiles: "identified_only",
        autocapture: true,
        capture_pageview: true,
        cross_subdomain_cookie: false,
        // Lazy-loaded extensions (recorder, surveys) stay off by default so
        // anonymous landing traffic doesn't pull ~82 KiB of JS it never uses.
        // Session recording is enabled programmatically after sign-in —
        // see RouterContextSyncInner below.
        capture_heatmaps: false,
        disable_session_recording: true,
        disable_surveys: true,
        capture_exceptions: {
          capture_unhandled_errors: true,
          capture_unhandled_rejections: true,
          capture_console_errors: false,
        },
        debug: import.meta.env.MODE === "development",
        opt_in_site_apps: true,
        bootstrap: {
          featureFlags: {
            apiAccessEnabled: true,
            tiktokAvailable: false,
            whatsappNotificationsEnabled: false,
          },
        },
      }}
    >
      <TRPCReactProvider>
        <TooltipProvider delayDuration={0} skipDelayDuration={0}>
          {children}
          {import.meta.env.DEV && <TailwindIndicator />}
        </TooltipProvider>
      </TRPCReactProvider>
    </PostHogProvider>
  );
}

/**
 * Syncs the session auth, user, and PostHog into the router context.
 * Must be rendered inside both RouterProvider AND ClientProviders
 * (i.e. in the root route component, not in Wrap).
 */
export function RouterContextSync({ children }: { children: React.ReactNode }) {
  // On the server, skip — router context is only needed client-side
  if (typeof window === "undefined") return <>{children}</>;

  return <RouterContextSyncInner>{children}</RouterContextSyncInner>;
}

const AUTH_ADJACENT_PATH =
  /^\/(app|account|admin|blueprint|blueprints|welcome|cli-auth|signin|signup)(\/|$)/;

// MODULE-LEVEL (not a per-component ref) on purpose: RouterContextSyncInner can
// remount (e.g. a mid-load router.update tearing down the /onboarding load), and
// a per-mount guard would re-arm and re-fire updateLocaleInfo on every remount.
// Because the mutation's global onSuccess invalidates the session queries, that
// re-fire re-seeds the teardown → an infinite remount loop (worst on a fresh
// Tank account, whose accountId is present from the first render). Module scope
// makes the sync fire once per userId for the tab's life, remount-proof.
const localeSyncedUserIds = new Set<string>();

function useLocaleSync(userId: string | null) {
  // retry: 2 covers the brief lag between the account being provisioned and
  // the cached bearer reflecting it. Without it the first attempt can fail
  // UNAUTHORIZED and the locale sync is lost permanently.
  const { mutate } = api.account.updateLocaleInfo.useMutation({ retry: 2 });

  React.useEffect(() => {
    if (!userId || localeSyncedUserIds.has(userId)) return;

    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone ?? null;
    const locale = navigator.language ?? null;

    mutate(
      { timezone, locale },
      {
        onSuccess: () => {
          localeSyncedUserIds.add(userId);
        },
      },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);
}

function RouterContextSyncInner({ children }: { children: React.ReactNode }) {
  const posthog = usePostHog();
  const { auth, user } = useSessionContext();
  const router = useRouter();

  const pathname = useRouterState({
    select: (state) => state.location.pathname,
  });
  const routerStatus = useRouterState({ select: (state) => state.status });

  // Gate on accountId, not just isSignedIn: updateLocaleInfo is a
  // protectedProcedure and rejects with UNAUTHORIZED until the account is
  // provisioned. The effect re-runs once it lands.
  const accountId = user.user?.publicMetadata?.accountId;
  useLocaleSync(user.isSignedIn && accountId ? (user.user?.id ?? null) : null);

  // Landing, blog, docs, and other public surfaces don't trigger session
  // recording — the recorder bundle stays out of the critical path there.
  // Recording starts on any auth-adjacent path (signin/signup to cover the
  // conversion funnel, app routes for product usage) OR once the user is
  // signed in anywhere. Gate on auth.isSignedIn (token-based source of truth)
  // not user.isSignedIn (identity-based, flips false while userinfo loads).
  React.useEffect(() => {
    // || is intentional: record on auth-adjacent paths even when isSignedIn is false
    const shouldRecord = auth.isSignedIn || AUTH_ADJACENT_PATH.test(pathname);
    if (shouldRecord) posthog.startSessionRecording();
  }, [posthog, auth.isSignedIn, pathname]);

  // PostHog identify
  React.useEffect(() => {
    const accountId = user.user?.publicMetadata.accountId;
    if (
      user.isSignedIn &&
      accountId &&
      user.user.primaryEmailAddress?.emailAddress
    ) {
      posthog.identify(accountId, {
        accountId,
        email: user.user.primaryEmailAddress.emailAddress,
        fullName: user.user.fullName,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    posthog,
    user.isSignedIn,
    user.user?.id,
    user.user?.publicMetadata.accountId,
    user.user?.primaryEmailAddress?.emailAddress,
  ]);

  // Synchronously update router context during render so it is available
  // to beforeLoad/loader. This must NOT be in a useEffect — beforeLoad
  // runs before effects fire, so the context would still be undefined.
  //
  // BUT only run router.update when the auth state semantically changes.
  // The session hooks can return new object references across renders, so an
  // unconditional router.update fires on every state flip (e.g. each
  // reload() from useIsAccountLoaded polling). If that happens while a
  // route's beforeLoad is in flight, it tears down the pending loadPromise
  // and React Suspense surfaces it as "Uncaught error undefined".
  //
  // AND never run router.update while a route load is in flight. Even a
  // genuinely semantic change (the one-time signed-out -> signed-in flip as
  // the session hydrates after login) tears down the pending loadPromise if
  // it lands mid-load — the recurring "blank first onboarding page until
  // refresh". The routes are already written to tolerate an unsynced context
  // during a load (the guards read the session imperatively rather than from
  // context.auth), so the update is stashed and applied as soon as the router
  // settles.
  const prevSyncedRef = React.useRef<{
    isSignedIn: boolean | undefined;
    userId: string | null | undefined;
    accountId: string | null | undefined;
  }>({
    isSignedIn: undefined,
    userId: undefined,
    accountId: undefined,
  });
  const hasDeferredSyncRef = React.useRef(false);
  // Latest values for the deferred apply — refreshed every render so the
  // effect below never installs a stale snapshot.
  const latestRef = React.useRef({ auth, user, posthog });
  latestRef.current = { auth, user, posthog };

  const nextSynced = {
    isSignedIn: auth.isSignedIn,
    userId: auth.userId,
    accountId: user.user?.publicMetadata?.accountId as
      | string
      | null
      | undefined,
  };
  if (
    prevSyncedRef.current.isSignedIn !== nextSynced.isSignedIn ||
    prevSyncedRef.current.userId !== nextSynced.userId ||
    prevSyncedRef.current.accountId !== nextSynced.accountId
  ) {
    if (routerStatus === "idle") {
      // This render applies the freshest values, superseding any sync that was
      // deferred while a load was in flight — clear the flag so the effect
      // below doesn't fire a redundant second router.update on the same settle.
      hasDeferredSyncRef.current = false;
      prevSyncedRef.current = nextSynced;
      router.update({
        context: {
          ...router.options.context,
          auth,
          user,
          posthog,
        },
      });
    } else {
      hasDeferredSyncRef.current = true;
    }
  }

  React.useEffect(() => {
    if (routerStatus !== "idle" || !hasDeferredSyncRef.current) return;
    hasDeferredSyncRef.current = false;
    const latest = latestRef.current;
    prevSyncedRef.current = {
      isSignedIn: latest.auth.isSignedIn,
      userId: latest.auth.userId,
      accountId: latest.user.user?.publicMetadata?.accountId as
        | string
        | null
        | undefined,
    };
    router.update({
      context: {
        ...router.options.context,
        auth: latest.auth,
        user: latest.user,
        posthog: latest.posthog,
      },
    });
  }, [routerStatus, router]);

  return <>{children}</>;
}
